import { Option, Schema } from "effect";

export const QuestionnaireBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const QUESTIONNAIRE_FREEFORM_MAX_LENGTH = 2_000;

const BoundedNameSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(80),
  Schema.makeFilter((value) => value.trim().length > 0 || "names must not be blank"),
  Schema.makeFilter(
    (value) => value === value.trim() || "names must not contain surrounding whitespace",
  ),
);
const BoundedLabelSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(240),
  Schema.makeFilter((value) => value.trim().length > 0 || "labels must not be blank"),
  Schema.makeFilter(
    (value) => value === value.trim() || "labels must not contain surrounding whitespace",
  ),
);
export const QuestionnaireTitleSchema = BoundedLabelSchema;
const BoundedShortcutSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(1),
  Schema.makeFilter((value) => /^[a-z0-9]$/iu.test(value) || "shortcuts must be letters or digits"),
);
const BoundedAnswerValueSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(QUESTIONNAIRE_FREEFORM_MAX_LENGTH),
  Schema.makeFilter((value) => value.trim().length > 0 || "answer values must not be blank"),
  Schema.makeFilter(
    (value) => value === value.trim() || "answer values must not contain surrounding whitespace",
  ),
);

const QuestionnaireIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(180),
  Schema.makeFilter((value) => value.trim().length > 0 || "questionnaire IDs must not be blank"),
  Schema.makeFilter(
    (value) =>
      value === value.trim() || "questionnaire IDs must not contain surrounding whitespace",
  ),
);

export const QuestionnaireChoiceSchema = Schema.Struct({
  value: BoundedNameSchema,
  label: BoundedLabelSchema,
  description: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500))),
  shortcut: Schema.optionalKey(BoundedShortcutSchema),
});

export const QuestionnaireItemSchema = Schema.Struct({
  name: BoundedNameSchema,
  prompt: BoundedLabelSchema,
  description: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(500))),
  required: Schema.Boolean,
  multiple: Schema.optionalKey(Schema.Boolean),
  allowFreeform: Schema.optionalKey(Schema.Boolean),
  choices: Schema.Array(QuestionnaireChoiceSchema).check(Schema.isMaxLength(12)),
}).check(
  Schema.makeFilter(
    (item) =>
      item.choices.length > 0 ||
      item.allowFreeform === true ||
      "questions must provide a fixed choice or allow a freeform answer",
  ),
  Schema.makeFilter(
    (item) =>
      new Set(item.choices.map((choice) => choice.value)).size === item.choices.length ||
      "choice values must be unique within an item",
  ),
  Schema.makeFilter(
    (item) =>
      item.allowFreeform !== true ||
      item.choices.length <= 11 ||
      "items with a freeform answer may define at most 11 fixed choices",
  ),
  Schema.makeFilter((item) => {
    const shortcuts = item.choices.flatMap((choice) =>
      choice.shortcut === undefined ? [] : [choice.shortcut.toLowerCase()],
    );
    return (
      new Set(shortcuts).size === shortcuts.length ||
      "choice shortcuts must be unique within an item"
    );
  }),
);

export const QuestionnaireItemsSchema = Schema.Array(QuestionnaireItemSchema)
  .check(Schema.isMinLength(1), Schema.isMaxLength(12))
  .check(
    Schema.makeFilter(
      (items) =>
        new Set(items.map((item) => item.name)).size === items.length ||
        "questionnaire item names must be unique",
    ),
  );

export const QuestionnaireDefinitionSchema = Schema.Struct({
  version: Schema.Literal("questionnaire/v1"),
  questionnaireId: QuestionnaireIdSchema,
  title: Schema.optionalKey(BoundedLabelSchema),
  items: QuestionnaireItemsSchema,
});

const QuestionnaireToolOutputPartSchema = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("tool-ask_user"),
    state: Schema.Literal("output-available"),
    output: QuestionnaireDefinitionSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("dynamic-tool"),
    toolName: Schema.Literal("ask_user"),
    state: Schema.Literal("output-available"),
    output: QuestionnaireDefinitionSchema,
  }),
]);

const decodeQuestionnaireToolOutputPart = Schema.decodeUnknownOption(
  QuestionnaireToolOutputPartSchema,
  {
    errors: "all",
    onExcessProperty: "ignore",
    reportInput: false,
  },
);

export function questionnaireDefinitionFromToolPart(
  value: unknown,
): QuestionnaireDefinition | undefined {
  const decoded = decodeQuestionnaireToolOutputPart(value);
  return Option.isSome(decoded) ? decoded.value.output : undefined;
}

const QuestionnaireItemAnswerSchema = Schema.Struct({
  name: BoundedNameSchema,
  values: Schema.Array(BoundedAnswerValueSchema).check(Schema.isMaxLength(12)),
  skipped: Schema.optionalKey(Schema.Boolean),
}).check(
  Schema.makeFilter(
    (answer) =>
      answer.skipped !== true || answer.values.length === 0 || "a skipped item has no values",
  ),
  Schema.makeFilter(
    (answer) =>
      new Set(answer.values).size === answer.values.length || "answer values must be unique",
  ),
);

export const QuestionnaireAnswerSchema = Schema.Struct({
  version: Schema.Literal("questionnaire-answer/v1"),
  questionnaireId: QuestionnaireIdSchema,
  answers: Schema.Array(QuestionnaireItemAnswerSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(12),
  ),
}).check(
  Schema.makeFilter(
    (answer) =>
      new Set(answer.answers.map((item) => item.name)).size === answer.answers.length ||
      "questionnaire answer names must be unique",
  ),
);

export const QuestionnaireDefinitionStandardSchema = Schema.toStandardSchemaV1(
  QuestionnaireDefinitionSchema,
  { parseOptions: QuestionnaireBoundaryParseOptions },
);

export const QuestionnaireAnswerStandardSchema = Schema.toStandardSchemaV1(
  QuestionnaireAnswerSchema,
  { parseOptions: QuestionnaireBoundaryParseOptions },
);

export const decodeQuestionnaireDefinition = Schema.decodeUnknownSync(
  QuestionnaireDefinitionSchema,
  QuestionnaireBoundaryParseOptions,
);
export const decodeQuestionnaireAnswer = Schema.decodeUnknownSync(
  QuestionnaireAnswerSchema,
  QuestionnaireBoundaryParseOptions,
);

export function validateQuestionnaireAnswerForDefinition(
  definition: QuestionnaireDefinition,
  answer: QuestionnaireAnswer,
): string | undefined {
  if (answer.questionnaireId !== definition.questionnaireId) {
    return "The answers do not belong to this questionnaire.";
  }
  if (answer.answers.length !== definition.items.length) {
    return "Every question must be answered or explicitly skipped.";
  }
  const answerByName = new Map(answer.answers.map((item) => [item.name, item] as const));
  if (answerByName.size !== answer.answers.length) return "Question answers must be unique.";
  if (answer.answers.some((item) => !definition.items.some((entry) => entry.name === item.name))) {
    return "The questionnaire contains an unknown answer.";
  }

  for (const item of definition.items) {
    const itemAnswer = answerByName.get(item.name);
    if (!itemAnswer) return `The answer for “${item.prompt}” is missing.`;
    if (itemAnswer.skipped === true) {
      if (item.required) return `“${item.prompt}” cannot be skipped.`;
      if (itemAnswer.values.length > 0) return "A skipped question cannot contain an answer.";
      continue;
    }
    if (itemAnswer.values.length === 0) {
      return item.required
        ? `“${item.prompt}” requires an answer.`
        : `“${item.prompt}” must be answered or explicitly skipped.`;
    }
    if (item.multiple !== true && itemAnswer.values.length > 1) {
      return `“${item.prompt}” accepts only one answer.`;
    }
    if (new Set(itemAnswer.values).size !== itemAnswer.values.length) {
      return `“${item.prompt}” contains a duplicate answer.`;
    }
    const allowedValues = new Set(item.choices.map((choice) => choice.value));
    const freeformValues = itemAnswer.values.filter((value) => !allowedValues.has(value));
    if (item.allowFreeform !== true && freeformValues.length > 0) {
      return `“${item.prompt}” contains an unknown choice.`;
    }
    if (freeformValues.length > 1) {
      return `“${item.prompt}” accepts only one freeform answer.`;
    }
  }
  return undefined;
}

export type QuestionnaireChoice = typeof QuestionnaireChoiceSchema.Type;
export type QuestionnaireItem = typeof QuestionnaireItemSchema.Type;
export type QuestionnaireDefinition = typeof QuestionnaireDefinitionSchema.Type;
export type QuestionnaireAnswer = typeof QuestionnaireAnswerSchema.Type;
